import { defineStore } from "pinia";
import { notificationService } from "@/services/NotificationService";

export const useNotificationStore = defineStore("notifications", {
  state: () => ({
    notifications: [], // mảng của TRANG HIỆN TẠI (res.content)
    isLoading: false,
    error: null,

    filterStatus: "all", // 'all' | 'unread' | 'read'
    currentPage: 1, // 1-based cho UI
    pageSize: 10,

    totalPages: 1, // meta từ server
    totalElements: 0,

    _idSet: new Set(), // chống trùng khi add real-time
  }),

  getters: {
    unreadCount(state) {
      const list = Array.isArray(state.notifications)
        ? state.notifications
        : [];
      return list.filter((n) => !n.daDoc).length;
    },
  },

  actions: {
    async fetchNotifications(page) {
      this.isLoading = true;
      this.error = null;
      try {
        const uiPage = page ?? this.currentPage ?? 1;
        const res = await notificationService.getNotifications({
          page: uiPage,
          size: this.pageSize,
          status: this.filterStatus,
        });

        // GÁN ĐÚNG MẢNG
        this.notifications = Array.isArray(res?.content) ? res.content : [];
        this.currentPage = (res?.number ?? Math.max(0, uiPage - 1)) + 1; // về 1-based
        this.pageSize = res?.size ?? this.pageSize;
        this.totalPages = res?.totalPages ?? 1;
        this.totalElements = res?.totalElements ?? this.notifications.length;

        this._idSet = new Set(this.notifications.map((n) => n.id));
      } catch (err) {
        console.error(err);
        this.error = "Không thể tải danh sách thông báo.";
        this.notifications = [];
        this.totalPages = 1;
        this.totalElements = 0;
      } finally {
        this.isLoading = false;
      }
    },

    setFilterStatus(status) {
      this.filterStatus = status;
      this.currentPage = 1;
      this.fetchNotifications(1);
    },

    setCurrentPage(page) {
      if (page > 0 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchNotifications(page);
      }
    },

    async handleMarkAsRead(notificationId) {
      const i = this.notifications.findIndex((n) => n.id === notificationId);
      if (i !== -1 && !this.notifications[i].daDoc) {
        const prev = this.notifications[i].daDoc;
        this.notifications[i].daDoc = true; // optimistic
        const ok = await notificationService.markAsRead(notificationId);
        if (!ok) this.notifications[i].daDoc = prev;
      }
    },

    async handleMarkAllAsRead() {
      const snapshot = this.notifications.map((n) => ({
        id: n.id,
        prev: n.daDoc,
      }));
      this.notifications.forEach((n) => (n.daDoc = true)); // optimistic
      const ok = await notificationService.markAllAsRead();
      if (!ok)
        snapshot.forEach(({ id, prev }) => {
          const i = this.notifications.findIndex((n) => n.id === id);
          if (i !== -1) this.notifications[i].daDoc = prev;
        });
    },

    // Real-time: chèn đầu trang 1, giữ pageSize
    addNotification(n) {
      if (!n || n.id == null) return;
      if (this._idSet.has(n.id)) return;

      if (this.currentPage === 1) {
        this.notifications.unshift(n);
        if (this.notifications.length > this.pageSize) {
          const removed = this.notifications.pop();
          if (removed) this._idSet.delete(removed.id);
        }
      }
      this.totalElements += 1;
      this.totalPages = Math.max(
        1,
        Math.ceil(this.totalElements / this.pageSize)
      );
      this._idSet.add(n.id);
    },

    reset() {
      this.notifications = [];
      this.isLoading = false;
      this.error = null;
      this.filterStatus = "all";
      this.currentPage = 1;
      this.pageSize = 10;
      this.totalPages = 1;
      this.totalElements = 0;
      this._idSet = new Set();
    },
  },
});
